import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
    useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Step5Props } from './types';
import { Step5ValueModel } from '../types';
import { validateStep5 } from '../Validation';
import { validateToC, handleRequestErrors } from '../../Validation'
import Summary from '../../Summary'
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors } from "@sharedComponents/PantaForm/utils";
import Panel from "@sharedComponents/Panel";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import Alert from "@sharedComponents/Alert";
import InputCheckbox from "@sharedComponents/InputCheckbox";
import Icon from "@sharedComponents/Icon";
import ButtonLinkBlock from "@sharedComponents/ButtonLinkBlock";

const Step5: FunctionComponent<Step5Props> = props => {
    const { t } = useTranslation()

    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])

    const termsRef = useRef<HTMLDivElement>(null)

    const allErrors = getAllErrors(formErrors)

    const [stepValues, setStepValues] = useState<Step5ValueModel>({
        terms: false,
    })

    const renderButtonLinkBlocks = (props: Step5Props) => {
        if (props.termsAndConditionsButtons && props.termsAndConditionsButtons.length > 0) {
            return props.termsAndConditionsButtons.map(buttonLinkBlock =>
                <ButtonLinkBlock key={buttonLinkBlock.ContentLink.Expanded?.Id} {...buttonLinkBlock.ContentLink.Expanded} />
            );
        }

        return;
    }

    const onChangeChkTerms = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked

        const termsText =
            termsRef && termsRef.current && termsRef.current.textContent

        setStepValues({
            ...stepValues,
            terms: value,
            termsText: termsText || '',
        })

        props.onChangeValues &&
            props.onChangeValues({ terms: value, termsText })

        const errors = validateToC(value, formErrors)

        setFormErrors(errors)
    }

    useEffect(() => {
        handleRequestErrors(formErrors, props.requestErrors).then(errors => {
            setFormErrors(errors)
        })
    }, [props.requestErrors])

    const termsHtml = (
        <div className={"form-step-wrapper__terms-wrapper-text"} ref={termsRef}>
            <div dangerouslySetInnerHTML={{__html: props.termsAndConditions}} />
            {renderButtonLinkBlocks(props)}
        </div>
    )

    const prevStep = () => {
        setFormErrors([])
        props.resetRequestErrors()
        props.prevStep && props.prevStep()
    }

    const onSubmit = () => {
        validateStep5(stepValues, []).then((errors: FormErrorType[]) => {
            if (errors && errors.length > 0) {
                setFormErrors(errors)
            } else {
                setFormErrors([])

                props.onSubmit && props.onSubmit()
            }
        })
    }

    const onCancel = () => {
        setFormErrors([])
        props.onCancel && props.onCancel()
    }

    return (
        <div className='form-step-wrapper'>
            <h3>{props.heading}</h3>
            {props.cmsContent ? (
                <div className='form-step-wrapper__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}
            <div className='form-step-wrapper__panel-container'>
                <Panel className="panel__preview">
                    <Summary sections={props.getSections(props.values)} />
                    <div className='form-step-wrapper__button-wrapper'>
                        <Button
                            type={ButtonType.PRIMARY_ALT}
                            className="edit-button"
                            text={t('global.change')}
                            disabled={props.submitting}
                            onClick={prevStep}
                        />
                    </div>
                </Panel>
            </div>
            <div className='form-step-wrapper__panel-container'>
                <Panel className="panel-terms">
                    <div className='form-step-wrapper__terms-wrapper'>
                        <InputCheckbox
                            id="optional-connection"
                            label={termsHtml}
                            onChange={onChangeChkTerms}
                            checked={Boolean(stepValues.terms)}
                            disableClickLabel={true}
                        />
                    </div>
                </Panel>
            </div>
            {allErrors.length > 0 && (
                <Alert
                    type="danger"
                    text={t('companyRegistration.companyInfoContainsErrors')}
                    subtext={allErrors}
                    compact={false}
                />
            )}
            {props.unexpectedErrors.length > 0 && (
                <Alert
                    type="danger"
                    text={''}
                    subtext={props.unexpectedErrors}
                    compact={true}
                />
            )}
            <div className='buttons-wrapper'>
                <Button
                    type={ButtonType.PRIMARY}
                    className="next-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <span>{t('button.submit')}</span>
                        </span>
                    }
                    onClick={onSubmit}
                    disabled={!stepValues.terms || props.submitting}
                    loading={props.submitting}
                />
                <Button
                    type={ButtonType.SECONDARY}
                    className="cancel-button"
                    text={
                        <span className='buttons-wrapper__button-text'>
                            <Icon iconName="close" />
                            <span>{t('button.cancel')}</span>
                        </span>
                    }
                    disabled={props.submitting}
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step5